/**
 * Admin: Homepage Preview Videos
 *
 * CRUD interface for public promotional preview videos shown on the landing
 * page. Backend enforces that these are PUBLIC content — never mixed with
 * protected class recordings, never require batch access.
 *
 * Key UX details:
 * - Vimeo URL / embed URL / bare numeric ID all accepted; backend extracts ID
 * - Featured is single-select (backend un-features siblings automatically)
 * - Each row has quick actions: Preview, Edit, Duplicate, Set Featured,
 *   Publish toggle, Delete
 * - Per-video CTAs: Primary registration, WhatsApp, OnPay (each optional)
 */
import { useEffect, useMemo, useState } from "react";
import { AdminLayout } from "@/components/Layouts";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ThumbnailUploader } from "@/components/ThumbnailUploader";
import { StatusBadge } from "@/components/StatusBadge";
import { EmptyState } from "@/components/EmptyState";
import { TradingChartLoader } from "@/components/TradingChartLoader";
import { Plus, Edit, Copy, Trash2, Star, Eye, Video, ExternalLink, MessageCircle, CreditCard } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = {
    id: null,
    title: "",
    description: "",
    thumbnail_url: "",
    vimeo_url: "",
    vimeo_video_id: "",
    course_name: "",
    instructor_name: "",
    duration: "",
    display_order: 999,
    is_featured: false,
    is_published: false,
    show_in_hero: false,
    show_in_preview_section: true,
    primary_cta_enabled: true,
    primary_cta_label: "Daftar Sekarang",
    primary_cta_url: "",
    whatsapp_cta_enabled: false,
    whatsapp_cta_label: "Tanya Admin",
    whatsapp_number: "",
    whatsapp_message: "Assalamualaikum, saya berminat untuk mendapatkan akses kelas rakaman di eLearningsaham.my.",
    whatsapp_url: "",
    onpay_cta_enabled: false,
    onpay_cta_label: "Daftar Melalui OnPay",
    onpay_url: "",
};

const asHttps = (u) => {
    if (!u) return "";
    const t = u.trim();
    if (!t) return "";
    if (t.startsWith("http://") || t.startsWith("https://")) return t;
    return `https://${t}`;
};

export default function AdminPreviewVideos() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [previewing, setPreviewing] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/preview-videos");
            setItems(r.data || []);
        } catch (err) {
            toast.error("Failed to load preview videos.");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const openCreate = () => { setEditing({ ...EMPTY }); setOpen(true); };
    const openEdit = (v) => { setEditing({ ...EMPTY, ...v }); setOpen(true); };

    const save = async () => {
        if (!editing.title?.trim()) return toast.error("Title required.");
        if (!editing.vimeo_url && !editing.vimeo_video_id) return toast.error("Vimeo URL or Vimeo video ID required.");
        try {
            const payload = { ...editing };
            // Ensure CTA URLs have protocol
            if (payload.primary_cta_url) payload.primary_cta_url = asHttps(payload.primary_cta_url);
            if (payload.onpay_url) payload.onpay_url = asHttps(payload.onpay_url);
            delete payload.id;
            if (editing.id) {
                await api.patch(`/admin/preview-videos/${editing.id}`, payload);
                toast.success("Preview video updated.");
            } else {
                await api.post("/admin/preview-videos", payload);
                toast.success("Preview video created.");
            }
            setOpen(false);
            load();
        } catch (err) {
            const msg = err?.response?.data?.detail || "Save failed.";
            toast.error(typeof msg === "string" ? msg : "Save failed.");
        }
    };

    const setFeatured = async (v) => {
        try {
            await api.patch(`/admin/preview-videos/${v.id}`, { is_featured: true, is_published: true });
            toast.success(`"${v.title}" is now the featured video.`);
            load();
        } catch { toast.error("Failed to set featured."); }
    };

    const togglePublish = async (v) => {
        try {
            await api.patch(`/admin/preview-videos/${v.id}`, { is_published: !v.is_published });
            toast.success(v.is_published ? "Unpublished." : "Published.");
            load();
        } catch { toast.error("Toggle failed."); }
    };

    const duplicate = async (v) => {
        try {
            await api.post(`/admin/preview-videos/${v.id}/duplicate`);
            toast.success("Duplicated as new draft.");
            load();
        } catch { toast.error("Duplicate failed."); }
    };

    const remove = async (v) => {
        if (!window.confirm(`Delete "${v.title}"? This can be restored from the database if needed.`)) return;
        try {
            await api.delete(`/admin/preview-videos/${v.id}`);
            toast.success("Deleted.");
            load();
        } catch { toast.error("Delete failed."); }
    };

    const featuredId = useMemo(() => items.find((v) => v.is_featured)?.id, [items]);

    return (
        <AdminLayout title="Homepage Preview Videos">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">Content Manager</p>
                    <h2 className="text-2xl font-heading font-semibold tracking-tight mt-1">Homepage Preview Videos</h2>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        Public promotional previews shown on the homepage. These are <strong>never</strong> mixed with protected class recordings.
                    </p>
                </div>
                <Button onClick={openCreate} className="btn-premium" data-testid="preview-add-btn">
                    <Plus className="h-4 w-4 mr-1" /> Add New Video
                </Button>
            </div>

            <Card className="glass-card">
                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-16 flex justify-center"><TradingChartLoader /></div>
                    ) : items.length === 0 ? (
                        <div className="p-8">
                            <EmptyState
                                icon={Video}
                                title="No preview videos yet"
                                description={'Belum ada video preview. Klik "Add New Video" untuk menambah video pertama.'}
                                testId="preview-empty"
                            />
                        </div>
                    ) : (
                        <Table data-testid="preview-table">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Video</TableHead>
                                    <TableHead>Vimeo ID</TableHead>
                                    <TableHead>Featured</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>CTAs</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {items.map((v) => (
                                    <TableRow key={v.id} data-testid={`preview-row-${v.id}`}>
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                {v.thumbnail_url ? (
                                                    <img src={fileUrl(v.thumbnail_url)} alt="" className="h-12 w-20 rounded-md object-cover border border-border" />
                                                ) : (
                                                    <div className="h-12 w-20 rounded-md bg-muted/50 border border-border flex items-center justify-center">
                                                        <Video className="h-4 w-4 text-muted-foreground" />
                                                    </div>
                                                )}
                                                <div className="min-w-0">
                                                    <p className="font-medium truncate">{v.title}</p>
                                                    <p className="text-xs text-muted-foreground truncate">{v.course_name || "—"}</p>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell><span className="font-mono text-xs">{v.vimeo_video_id}</span></TableCell>
                                        <TableCell>
                                            {v.is_featured ? (
                                                <span className="inline-flex items-center gap-1 text-primary font-semibold text-xs">
                                                    <Star className="h-3.5 w-3.5 fill-current" /> Featured
                                                </span>
                                            ) : (
                                                <Button size="sm" variant="ghost" onClick={() => setFeatured(v)} data-testid={`preview-feature-${v.id}`}>
                                                    <Star className="h-3.5 w-3.5" /> Set
                                                </Button>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <button onClick={() => togglePublish(v)} data-testid={`preview-publish-${v.id}`} className="cursor-pointer">
                                                <StatusBadge status={v.is_published ? "active" : "pending"} label={v.is_published ? "Published" : "Draft"} />
                                            </button>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1.5 text-[11px]">
                                                {v.primary_cta_enabled && v.primary_cta_url && (
                                                    <span title="Primary CTA" className="px-1.5 py-0.5 rounded bg-primary/15 text-primary border border-primary/30">Reg</span>
                                                )}
                                                {v.whatsapp_cta_enabled && v.whatsapp_number && (
                                                    <span title="WhatsApp CTA" className="px-1.5 py-0.5 rounded bg-green-500/15 text-green-500 border border-green-500/30">WA</span>
                                                )}
                                                {v.onpay_cta_enabled && v.onpay_url && (
                                                    <span title="OnPay CTA" className="px-1.5 py-0.5 rounded bg-gold/15 text-gold border border-gold/30">OnPay</span>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right whitespace-nowrap">
                                            <Button size="sm" variant="ghost" onClick={() => setPreviewing(v)} data-testid={`preview-view-${v.id}`}>
                                                <Eye className="h-3.5 w-3.5" />
                                            </Button>
                                            <Button size="sm" variant="ghost" onClick={() => openEdit(v)} data-testid={`preview-edit-${v.id}`}>
                                                <Edit className="h-3.5 w-3.5" />
                                            </Button>
                                            <Button size="sm" variant="ghost" onClick={() => duplicate(v)} data-testid={`preview-duplicate-${v.id}`}>
                                                <Copy className="h-3.5 w-3.5" />
                                            </Button>
                                            <Button size="sm" variant="ghost" onClick={() => remove(v)} data-testid={`preview-delete-${v.id}`}>
                                                <Trash2 className="h-3.5 w-3.5 text-destructive" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* EDIT DIALOG */}
            {editing && (
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="preview-dialog">
                        <DialogHeader>
                            <DialogTitle>{editing.id ? "Edit Preview Video" : "Add New Preview Video"}</DialogTitle>
                            <DialogDescription>Public homepage promo. No login or batch access required.</DialogDescription>
                        </DialogHeader>

                        <div className="grid gap-5 py-2">
                            {/* Video Information */}
                            <section className="space-y-3">
                                <h3 className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Video Information</h3>
                                <div className="grid sm:grid-cols-2 gap-3">
                                    <div className="sm:col-span-2">
                                        <Label>Title *</Label>
                                        <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="preview-title" />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <Label>Short description</Label>
                                        <Textarea rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="preview-desc" />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <Label>Vimeo URL, embed URL, or bare video ID *</Label>
                                        <Input
                                            placeholder="https://vimeo.com/123456789  •  123456789"
                                            value={editing.vimeo_url || editing.vimeo_video_id}
                                            onChange={(e) => setEditing({ ...editing, vimeo_url: e.target.value, vimeo_video_id: "" })}
                                            data-testid="preview-vimeo"
                                        />
                                        <p className="text-[11px] text-muted-foreground mt-1">The ID is auto-extracted from any Vimeo URL form.</p>
                                    </div>
                                    <div>
                                        <Label>Course name</Label>
                                        <Input value={editing.course_name} onChange={(e) => setEditing({ ...editing, course_name: e.target.value })} data-testid="preview-course" />
                                    </div>
                                    <div>
                                        <Label>Instructor name</Label>
                                        <Input value={editing.instructor_name} onChange={(e) => setEditing({ ...editing, instructor_name: e.target.value })} data-testid="preview-instructor" />
                                    </div>
                                    <div>
                                        <Label>Duration</Label>
                                        <Input placeholder="e.g. 12 min" value={editing.duration} onChange={(e) => setEditing({ ...editing, duration: e.target.value })} data-testid="preview-duration" />
                                    </div>
                                    <div>
                                        <Label>Display order</Label>
                                        <Input type="number" value={editing.display_order} onChange={(e) => setEditing({ ...editing, display_order: parseInt(e.target.value || "0", 10) })} data-testid="preview-order" />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <Label>Thumbnail image</Label>
                                        <ThumbnailUploader
                                            value={editing.thumbnail_url}
                                            onChange={(v) => setEditing({ ...editing, thumbnail_url: v })}
                                            kind="thumbnail"
                                            testId="preview-thumb"
                                        />
                                    </div>
                                </div>
                                <div className="flex items-center gap-6 pt-2 flex-wrap">
                                    <div className="flex items-center gap-2">
                                        <Switch checked={!!editing.is_published} onCheckedChange={(v) => setEditing({ ...editing, is_published: v })} id="pv-pub" data-testid="preview-published" />
                                        <Label htmlFor="pv-pub">Published</Label>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Switch checked={!!editing.is_featured} onCheckedChange={(v) => setEditing({ ...editing, is_featured: v })} id="pv-feat" data-testid="preview-featured" />
                                        <Label htmlFor="pv-feat">Featured video</Label>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Switch checked={!!editing.show_in_hero} onCheckedChange={(v) => setEditing({ ...editing, show_in_hero: v })} id="pv-hero" />
                                        <Label htmlFor="pv-hero">Show in hero</Label>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Switch checked={!!editing.show_in_preview_section} onCheckedChange={(v) => setEditing({ ...editing, show_in_preview_section: v })} id="pv-sec" />
                                        <Label htmlFor="pv-sec">Show in preview section</Label>
                                    </div>
                                </div>
                                {editing.is_featured && featuredId && featuredId !== editing.id && (
                                    <p className="text-xs text-amber-500 flex items-center gap-1">
                                        <Star className="h-3 w-3" /> Setting this featured will un-feature the currently-featured video.
                                    </p>
                                )}
                            </section>

                            {/* Primary CTA */}
                            <section className="rounded-xl border border-border/60 bg-muted/20 p-4 space-y-3">
                                <div className="flex items-center justify-between gap-3">
                                    <h3 className="text-sm font-semibold flex items-center gap-2">
                                        <div className="icon-tile icon-tile-teal h-7 w-7"><CreditCard className="h-3.5 w-3.5" /></div>
                                        Primary CTA (Registration)
                                    </h3>
                                    <Switch checked={!!editing.primary_cta_enabled} onCheckedChange={(v) => setEditing({ ...editing, primary_cta_enabled: v })} data-testid="preview-cta-primary-enabled" />
                                </div>
                                {editing.primary_cta_enabled && (
                                    <div className="grid sm:grid-cols-2 gap-3">
                                        <div><Label>Button label</Label><Input value={editing.primary_cta_label} onChange={(e) => setEditing({ ...editing, primary_cta_label: e.target.value })} /></div>
                                        <div><Label>URL</Label><Input placeholder="https://elearningsaham.my/register" value={editing.primary_cta_url} onChange={(e) => setEditing({ ...editing, primary_cta_url: e.target.value })} /></div>
                                    </div>
                                )}
                            </section>

                            {/* WhatsApp CTA */}
                            <section className="rounded-xl border border-border/60 bg-muted/20 p-4 space-y-3">
                                <div className="flex items-center justify-between gap-3">
                                    <h3 className="text-sm font-semibold flex items-center gap-2">
                                        <div className="icon-tile icon-tile-teal h-7 w-7"><MessageCircle className="h-3.5 w-3.5" /></div>
                                        WhatsApp CTA
                                    </h3>
                                    <Switch checked={!!editing.whatsapp_cta_enabled} onCheckedChange={(v) => setEditing({ ...editing, whatsapp_cta_enabled: v })} data-testid="preview-cta-wa-enabled" />
                                </div>
                                {editing.whatsapp_cta_enabled && (
                                    <div className="grid sm:grid-cols-2 gap-3">
                                        <div><Label>Button label</Label><Input value={editing.whatsapp_cta_label} onChange={(e) => setEditing({ ...editing, whatsapp_cta_label: e.target.value })} /></div>
                                        <div><Label>Phone number</Label><Input placeholder="+60123456789" value={editing.whatsapp_number} onChange={(e) => setEditing({ ...editing, whatsapp_number: e.target.value })} /></div>
                                        <div className="sm:col-span-2"><Label>Pre-filled message</Label><Textarea rows={2} value={editing.whatsapp_message} onChange={(e) => setEditing({ ...editing, whatsapp_message: e.target.value })} /></div>
                                    </div>
                                )}
                            </section>

                            {/* OnPay CTA */}
                            <section className="rounded-xl border border-border/60 bg-muted/20 p-4 space-y-3">
                                <div className="flex items-center justify-between gap-3">
                                    <h3 className="text-sm font-semibold flex items-center gap-2">
                                        <div className="icon-tile icon-tile-gold h-7 w-7"><ExternalLink className="h-3.5 w-3.5" /></div>
                                        OnPay CTA (optional)
                                    </h3>
                                    <Switch checked={!!editing.onpay_cta_enabled} onCheckedChange={(v) => setEditing({ ...editing, onpay_cta_enabled: v })} data-testid="preview-cta-onpay-enabled" />
                                </div>
                                {editing.onpay_cta_enabled && (
                                    <div className="grid sm:grid-cols-2 gap-3">
                                        <div><Label>Button label</Label><Input value={editing.onpay_cta_label} onChange={(e) => setEditing({ ...editing, onpay_cta_label: e.target.value })} /></div>
                                        <div><Label>OnPay URL</Label><Input placeholder="https://onpay.my/..." value={editing.onpay_url} onChange={(e) => setEditing({ ...editing, onpay_url: e.target.value })} /></div>
                                    </div>
                                )}
                            </section>
                        </div>

                        <DialogFooter>
                            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                            <Button className="btn-premium" onClick={save} data-testid="preview-save-btn">Save</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}

            {/* PREVIEW MODAL */}
            {previewing && (
                <Dialog open={!!previewing} onOpenChange={(v) => !v && setPreviewing(null)}>
                    <DialogContent className="max-w-3xl">
                        <DialogHeader>
                            <DialogTitle>{previewing.title}</DialogTitle>
                            <DialogDescription>{previewing.course_name} {previewing.instructor_name && `· ${previewing.instructor_name}`}</DialogDescription>
                        </DialogHeader>
                        <div className="aspect-video rounded-xl overflow-hidden border border-border">
                            <iframe
                                src={previewing.vimeo_url}
                                className="w-full h-full"
                                allow="autoplay; fullscreen; picture-in-picture"
                                allowFullScreen
                                title={previewing.title}
                            />
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </AdminLayout>
    );
}
